/* ═══════════════════════════════════════════════════════════
   PEAKVIEW ALPINE — main
   GSAP orchestration · expedition systems
   ═══════════════════════════════════════════════════════════ */

const $ = (s, c = document) => c.querySelector(s);
const $$ = (s, c = document) => [...c.querySelectorAll(s)];
/* dev flag: ?static=1 renders final states with no animation (design QA) */
const STATIC = new URLSearchParams(location.search).has("static");
const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches || STATIC;
const finePointer = window.matchMedia("(pointer: fine) and (min-width: 901px)").matches;
const hasGSAP = typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";

const SUMMITS = [
  { name: "EVEREST", coords: "27.9881° N · 86.9250° E" },
  { name: "K2", coords: "35.8825° N · 76.5133° E" },
  { name: "KILIMANJARO", coords: "3.0674° S · 37.3556° E" },
  { name: "MONT BLANC", coords: "45.8326° N · 6.8652° E" },
  { name: "FUJI", coords: "35.3606° N · 138.7274° E" },
  { name: "ACONCAGUA", coords: "32.6532° S · 70.0109° W" },
  { name: "DENALI", coords: "63.0692° N · 151.0070° W" },
];
const SUMMIT_ALT = 8849;

/* Seven frames — one chassis, seven dedications */
const FRAMES = {
  evr: { n: 1, code: "EVR", name: "EVEREST", ded: "The Highest", elev: "8,849", lat: "27.9881° N", lng: "86.9250° E", range: "HIMALAYAS",
    story: "The roof of the world — 8,849 metres of thin air and long shadows. Everest asks for patience, then takes everything else. N° 01 is dedicated to the summit every other summit is measured against." },
  k2: { n: 2, code: "K2", name: "K2", ded: "The Savage", elev: "8,611", lat: "35.8825° N", lng: "76.5133° E", range: "KARAKORAM",
    story: "At 8,611 metres, K2 is the world's second highest peak and one of the most demanding climbs on Earth. Unforgiving. Technical. Uncompromising. N° 02 was designed in that spirit — for those who seek the summit and accept nothing less." },
  kil: { n: 3, code: "KIL", name: "KILIMANJARO", ded: "The Icon", elev: "5,895", lat: "3.0674° S", lng: "37.3556° E", range: "TANZANIA",
    story: "A solitary volcano above the savanna — the highest free-standing mountain on Earth, and the summit where PeakView began. Reach the top, and carry the clarity back down." },
  mbl: { n: 4, code: "MBL", name: "MONT BLANC", ded: "The Classic", elev: "4,808", lat: "45.8326° N", lng: "6.8652° E", range: "THE ALPS",
    story: "The birthplace of alpinism — 4,808 metres of granite and ice above Chamonix. Elegant, storied, never simple. N° 04 is the standard everything alpine is measured by." },
  fuj: { n: 5, code: "FUJ", name: "FUJI", ded: "The Visionary", elev: "3,776", lat: "35.3606° N", lng: "138.7274° E", range: "HONSHU · JAPAN",
    story: "A perfect cone held sacred for a thousand years. Fuji is climbed in darkness to meet the sunrise — proof that the view was always the point." },
  acn: { n: 6, code: "ACN", name: "ACONCAGUA", ded: "The Colossus", elev: "6,961", lat: "32.6532° S", lng: "70.0109° W", range: "ANDES",
    story: "The Sentinel of the Andes — the highest peak outside Asia. A giant of wind and scree that rewards one thing above all: relentlessness." },
  dnl: { n: 7, code: "DNL", name: "DENALI", ded: "The Wild", elev: "6,190", lat: "63.0692° N", lng: "151.0070° W", range: "ALASKA RANGE",
    story: "The Great One — 6,190 metres rising straight off the tundra, further from help than anywhere in the Alps. Cold, vast, and honest." },
};
const FRAME_ORDER = ["evr", "k2", "kil", "mbl", "fuj", "acn", "dnl"];

/* ── safety: never trap the page behind the preloader ── */
function forceReveal() {
  const pre = $("#preloader");
  if (pre) pre.style.display = "none";
  document.documentElement.classList.add("no-anim");
}
if (!hasGSAP) forceReveal();
setTimeout(() => {
  const pre = $("#preloader");
  if (pre && document.contains(pre) && !window.__pvPreDone) {
    pre.remove();
    document.documentElement.classList.add("no-anim", "static");
    if (hasGSAP) gsap.set("[data-hero-fade], .nav, .hero-title .char", { clearProps: "all" });
    window.__pvFinishBoot?.();
  }
}, 8000);

/* ── text scramble ───────────────────────── */
const GLYPHS = "▲△/\\|—·°0123456789ACEFHIKLMNOPRSTUVX";
function scramble(el, target, dur = 0.7) {
  if (!el) return;
  const from = el.textContent;
  const len = Math.max(from.length, target.length);
  const start = performance.now();
  function tick(now) {
    const p = Math.min((now - start) / (dur * 1000), 1);
    let out = "";
    for (let i = 0; i < len; i++) {
      const revealAt = (i / len) * 0.8;
      if (p >= revealAt + 0.2) out += target[i] || "";
      else if (p >= revealAt) out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
      else out += from[i] || GLYPHS[(Math.random() * GLYPHS.length) | 0];
    }
    el.textContent = out;
    if (p < 1) requestAnimationFrame(tick);
    else el.textContent = target;
  }
  requestAnimationFrame(tick);
}

/* ── split helpers ───────────────────────── */
function splitChars(el) {
  const text = el.textContent;
  el.textContent = "";
  return [...text].map((ch) => {
    const s = document.createElement("span");
    s.className = "char";
    s.textContent = ch;
    el.appendChild(s);
    return s;
  });
}
function splitWords(el) {
  const words = el.textContent.trim().split(/\s+/);
  el.textContent = "";
  return words.map((w, i) => {
    const mask = document.createElement("span");
    mask.className = "split-line";
    mask.style.display = "inline-block";
    const inner = document.createElement("span");
    inner.textContent = w;
    mask.appendChild(inner);
    el.appendChild(mask);
    if (i < words.length - 1) el.appendChild(document.createTextNode(" "));
    return inner;
  });
}

/* ── SVG stroke prep ─────────────────────── */
function prepStroke(path) {
  try {
    const len = path.getTotalLength();
    path.style.strokeDasharray = len;
    path.style.strokeDashoffset = len;
    return len;
  } catch { return 0; }
}

/* ── spindrift — altitude-reactive alpine wind ────────────────
   Not falling dots: wind-driven snow. Particles are motion-streaked
   along their velocity, layered in three depth planes (near streaks
   fast + bright, far haze slow + dim), pushed by a turbulent wind
   field (two sine octaves + curl-ish noise) with gust surges.
   Intensity 0..1 comes from scroll altitude: still air at the hero,
   first spindrift past ~4,200 m, storm at the summit.            */
function initSnow() {
  const canvas = $("#snow");
  if (!canvas || (reduced && !STATIC)) { canvas?.remove(); return; }
  const ctx = canvas.getContext("2d", { alpha: true });
  let w, h, parts = [], veils = [];
  const DPR = Math.min(devicePixelRatio || 1, 2);

  const state = { target: 0, current: 0, gust: 1 };
  window.__pvSetSnow = (v) => { state.target = Math.max(0, Math.min(1, v)); };
  window.__pvGust = (v) => { state.gust = v; };

  /* depth planes: far grains stay points, near ones stretch into streaks */
  const PLANES = [
    { spd: 0.3, size: 0.62, a: 0.13, streak: 0.0 },   // far haze — grains only
    { spd: 0.62, size: 0.95, a: 0.22, streak: 0.55 }, // mid
    { spd: 1.15, size: 1.35, a: 0.34, streak: 1.25 }, // near, fast streaks
  ];

  function spawn(p, seeded) {
    const plane = PLANES[(Math.random() * PLANES.length) | 0];
    p.pl = plane;
    /* enter from the left/top edge — wind blows across, not straight down */
    p.x = seeded ? Math.random() * w : -30 - Math.random() * 120;
    p.y = seeded ? Math.random() * h : Math.random() * h * 1.15 - h * 0.1;
    p.r = (0.45 + Math.random() * 1.05) * plane.size;
    p.vx = 0; p.vy = 0;
    p.ph = Math.random() * Math.PI * 2;
    p.spin = 0.5 + Math.random() * 1.4;
    p.a = (0.5 + Math.random() * 0.7) * plane.a;
    return p;
  }
  function spawnVeil(v, seeded) {
    v.w = w * (0.45 + Math.random() * 0.55);
    v.h = 90 + Math.random() * 190;
    v.x = seeded ? Math.random() * w - v.w * 0.5 : -v.w;
    v.y = Math.random() * (h + v.h) - v.h * 0.5;
    v.spd = 0.25 + Math.random() * 0.5;
    v.a = 0.05 + Math.random() * 0.07;
    return v;
  }

  function size() {
    w = innerWidth; h = innerHeight;
    canvas.width = w * DPR; canvas.height = h * DPR;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    const target = Math.min(Math.round((w * h) / 3400), 460);
    parts = Array.from({ length: target }, () => spawn({}, true));
    veils = Array.from({ length: 5 }, () => spawnVeil({}, true));
  }

  /* turbulent wind field — cheap flow noise, no per-frame allocation */
  function windAt(x, y, t) {
    const gx = Math.sin(y * 0.011 + t * 0.6) * 0.5 + Math.sin(y * 0.004 - t * 0.31) * 0.5;
    const gy = Math.sin(x * 0.008 - t * 0.42) * 0.5 + Math.sin(x * 0.017 + t * 0.71) * 0.35;
    return { gx, gy };
  }

  let last = performance.now();
  function frame(now) {
    requestAnimationFrame(frame);
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    const t = now * 0.001;

    state.current += (state.target * state.gust - state.current) * Math.min(dt * 1.8, 1);
    const I = Math.min(state.current, 1.3);

    ctx.clearRect(0, 0, w, h);
    if (I < 0.02) return;

    /* base wind: rises with intensity, surges in slow gust waves */
    const gustWave = 0.7 + 0.3 * Math.sin(t * 0.37) + 0.18 * Math.sin(t * 0.13 + 1.7);
    const baseWind = (90 + 330 * I) * gustWave;
    const fall = 26 + 40 * I;

    /* drifting mist — soft elliptical veils behind the particles */
    if (I > 0.35) {
      const veilI = (I - 0.35) * 1.5;
      for (const v of veils) {
        v.x += baseWind * v.spd * 0.16 * dt;
        if (v.x - v.w > w) spawnVeil(v, false);
        const cx = v.x + v.w / 2, cy = v.y + v.h / 2, rx = v.w / 2, ry = v.h / 2;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(1, ry / rx);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
        g.addColorStop(0, `rgba(214,232,246,${v.a * veilI})`);
        g.addColorStop(0.6, `rgba(214,232,246,${v.a * veilI * 0.45})`);
        g.addColorStop(1, "rgba(214,232,246,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, rx, 0, 7);
        ctx.fill();
        ctx.restore();
      }
    }

    const active = Math.min(parts.length, Math.round(parts.length * Math.min(I * 1.05, 1)));
    ctx.lineCap = "round";
    for (let i = 0; i < active; i++) {
      const p = parts[i];
      const pl = p.pl;
      const { gx, gy } = windAt(p.x, p.y, t);
      p.ph += dt * p.spin;

      /* velocity: wind + turbulence + gravity, scaled by depth plane */
      const tvx = (baseWind + gx * 120 * I) * pl.spd;
      const tvy = (fall + gy * 55 * I + Math.sin(p.ph) * 14) * pl.spd;
      p.vx += (tvx - p.vx) * Math.min(dt * 3.2, 1);
      p.vy += (tvy - p.vy) * Math.min(dt * 3.2, 1);
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      if (p.x > w + 40 || p.y > h + 40) { spawn(p, false); continue; }

      /* motion streak along velocity — the thing that reads as wind.
         Short and soft: long bright lines read as rain, not spindrift. */
      const sp = Math.hypot(p.vx, p.vy);
      const len = Math.min(sp * 0.016 * pl.streak * (0.35 + I * 0.5), 13);
      const alpha = Math.min(p.a * (0.3 + I * 0.7), 0.5);
      if (len > 2) {
        ctx.strokeStyle = `rgba(226,240,252,${alpha})`;
        ctx.lineWidth = p.r;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x - (p.vx / sp) * len, p.y - (p.vy / sp) * len);
        ctx.stroke();
      } else {
        ctx.fillStyle = `rgba(226,240,252,${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, 7);
        ctx.fill();
      }
    }
  }
  size();
  addEventListener("resize", size);
  requestAnimationFrame(frame);
  /* QA hook: advance the sim by hand when rAF is unavailable (?static=1) */
  if (STATIC) window.__pvSnowTick = (steps = 90) => {
    let t = performance.now();
    for (let i = 0; i < steps; i++) { t += 16.7; last = t - 16.7; frame(t); }
    return "ticked " + steps;
  };
}

/* ── cursor + magnetics ──────────────────── */
function initCursor() {
  if (!finePointer || !hasGSAP || reduced) return;
  const wrap = $(".cursor"), dot = $(".cursor-dot"), ring = $(".cursor-ring"), label = $(".cursor-label");
  if (!wrap) return;
  /* only now that the custom cursor is truly running do we let CSS hide the
     native cursor — reduced-motion / static / GSAP-fail / touch users (which
     all return early above) keep the normal system pointer (see css .cursor-live) */
  document.documentElement.classList.add("cursor-live");
  const dx = gsap.quickTo(dot, "x", { duration: 0.12, ease: "power2.out" });
  const dy = gsap.quickTo(dot, "y", { duration: 0.12, ease: "power2.out" });
  const rx = gsap.quickTo(ring, "x", { duration: 0.45, ease: "power3.out" });
  const ry = gsap.quickTo(ring, "y", { duration: 0.45, ease: "power3.out" });
  addEventListener("pointermove", (e) => { dx(e.clientX); dy(e.clientY); rx(e.clientX); ry(e.clientY); });

  const hoverables = "a, button, input, [data-cursor]";
  document.addEventListener("pointerover", (e) => {
    const t = e.target.closest(hoverables);
    if (!t) return;
    /* quiet zones (e.g. the Journal editorial grid): the cursor stays a
       plain dot + ring — no filled or enlarged hover circle */
    if (t.closest("[data-cursor-quiet]")) return;
    const mode = t.dataset.cursor;
    wrap.classList.add("is-active");
    label.textContent = mode ? mode.toUpperCase() : "";
    gsap.to(ring, { scale: mode ? 2.1 : 1.5, duration: 0.35, ease: "power3.out" });
    gsap.to(dot, { scale: 0.4, duration: 0.3 });
  });
  document.addEventListener("pointerout", (e) => {
    if (!e.target.closest(hoverables)) return;
    wrap.classList.remove("is-active");
    label.textContent = "";
    gsap.to(ring, { scale: 1, duration: 0.35, ease: "power3.out" });
    gsap.to(dot, { scale: 1, duration: 0.3 });
  });

  $$("[data-magnetic]").forEach((el) => {
    const mx = gsap.quickTo(el, "x", { duration: 0.4, ease: "power3.out" });
    const my = gsap.quickTo(el, "y", { duration: 0.4, ease: "power3.out" });
    el.addEventListener("pointermove", (e) => {
      const r = el.getBoundingClientRect();
      mx((e.clientX - r.left - r.width / 2) * 0.22);
      my((e.clientY - r.top - r.height / 2) * 0.32);
    });
    el.addEventListener("pointerleave", () => {
      gsap.to(el, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1, 0.45)" });
    });
  });
}

/* ── nav ─────────────────────────────────── */
function initNav(lenis) {
  const nav = $("#nav");
  let lastY = 0;
  function onScroll(y) {
    nav.classList.toggle("nav--scrolled", y > 40);
    if (y > 600 && y > lastY + 6) nav.classList.add("nav--hidden");
    else if (y < lastY - 4 || y < 600) nav.classList.remove("nav--hidden");
    lastY = y;
  }
  if (lenis) lenis.on("scroll", ({ scroll }) => onScroll(scroll));
  else addEventListener("scroll", () => onScroll(scrollY), { passive: true });

  /* smooth anchors */
  $$('a[href^="#"]').forEach((a) => {
    a.addEventListener("click", (e) => {
      const id = a.getAttribute("href");
      const target = id === "#top" ? document.body : $(id);
      if (!target) return;
      e.preventDefault();
      closeMenu();
      if (lenis) lenis.scrollTo(id === "#top" ? 0 : target, { offset: -70, duration: 1.6 });
      else target.scrollIntoView({ behavior: "smooth" });
    });
  });

  /* scramble hover on nav links */
  $$("[data-scramble]").forEach((el) => {
    const orig = el.textContent;
    el.addEventListener("pointerenter", () => scramble(el, orig, 0.5));
  });

  /* menu overlay */
  const burger = $("#burger"), menu = $("#menu");
  const menuBg = [$("#main"), $("footer")].filter(Boolean);
  let open = false;

  /* focus containment while the full-screen overlay is open: Escape closes it,
     Tab is trapped within the menu links, the background is inert (and hidden
     from AT), and focus returns to the burger on close */
  const menuTrap = (e) => {
    if (!open) return;
    if (e.key === "Escape") { e.preventDefault(); window.closeMenu(); return; }
    if (e.key !== "Tab") return;
    const links = $$(".menu-link", menu);
    if (!links.length) return;
    const first = links[0], last = links[links.length - 1], a = document.activeElement;
    if (e.shiftKey && (a === first || !menu.contains(a))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (a === last || !menu.contains(a))) { e.preventDefault(); first.focus(); }
  };

  window.closeMenu = () => {
    if (!open) return;
    open = false;
    burger.classList.remove("open");
    burger.setAttribute("aria-expanded", "false");
    burger.setAttribute("aria-label", "Open menu");
    menu.setAttribute("aria-hidden", "true");
    document.removeEventListener("keydown", menuTrap);
    menuBg.forEach((el) => { el.removeAttribute("inert"); el.removeAttribute("aria-hidden"); });
    if (hasGSAP && !reduced) gsap.to(menu, { autoAlpha: 0, duration: 0.4, ease: "power2.inOut", onComplete: () => menu.classList.remove("open") });
    else { menu.style.opacity = ""; menu.classList.remove("open"); }
    burger.focus();
  };
  burger?.addEventListener("click", () => {
    open = !open;
    burger.classList.toggle("open", open);
    burger.setAttribute("aria-expanded", String(open));
    burger.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    menu.setAttribute("aria-hidden", String(!open));
    if (open) {
      menu.classList.add("open");
      menuBg.forEach((el) => { el.setAttribute("inert", ""); el.setAttribute("aria-hidden", "true"); });
      document.addEventListener("keydown", menuTrap);
      if (hasGSAP && !reduced) {
        gsap.fromTo(menu, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.45, ease: "power2.out" });
        gsap.fromTo(".menu-link", { y: 34, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.7, stagger: 0.06, ease: "power3.out", delay: 0.08 });
      } else menu.style.opacity = "1";
      /* move focus into the overlay once it is visible (autoAlpha toggles
         visibility, so wait a beat before focusing the first link) */
      const links = $$(".menu-link", menu);
      if (links[0]) setTimeout(() => { if (open) links[0].focus(); }, 120);
    } else window.closeMenu();
  });
}

/* ── marquee ─────────────────────────────── */
function initMarquee() {
  const track = $("#marquee-track");
  if (!track || !hasGSAP || reduced) return;
  const seg = track.children[0];
  const segW = seg.offsetWidth;
  while (track.scrollWidth < innerWidth * 2.5) track.appendChild(seg.cloneNode(true));
  const wrap = gsap.utils.wrap(-segW, 0);
  let x = 0;
  /* slower drift — an expedition transmission, not a fashion ticker */
  gsap.ticker.add((_, dt) => {
    x -= dt * 0.026;
    gsap.set(track, { x: wrap(x) });
  });
}

/* ── generic reveals ─────────────────────── */
function initReveals() {
  if (!hasGSAP || reduced) return;
  $$("[data-reveal]").forEach((el) => {
    gsap.from(el, {
      y: 34, autoAlpha: 0,
      duration: 1.15, ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 88%" },
    });
  });
  $$("[data-split]").forEach((el) => {
    const words = splitWords(el);
    gsap.from(words, {
      yPercent: 115,
      duration: 1.05, stagger: 0.07, ease: "power4.out",
      scrollTrigger: { trigger: el, start: "top 86%" },
    });
  });
}

/* ── hero ────────────────────────────────── */
function heroIntroTimeline(chars, sceneApi) {
  const tl = gsap.timeline();
  if (chars.length) tl.to(chars, { yPercent: 0, duration: 1.25, stagger: 0.045, ease: "expo.out" }, 0);
  tl.fromTo("[data-hero-fade]", { y: 26, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 1.1, stagger: 0.09, ease: "power3.out" }, 0.35)
    .fromTo(".nav", { y: -26, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.9, ease: "power3.out", clearProps: "transform" }, 0.5);
  if (sceneApi) tl.to({ p: 0 }, { p: 1, duration: 2.4, ease: "power2.out", onUpdate() { sceneApi.setIntro(this.targets()[0].p); } }, 0);
  return tl;
}

function initHeroScroll(sceneApi) {
  if (!hasGSAP || reduced || !$(".hero")) return;
  gsap.to(".hero-content", {
    yPercent: -16, autoAlpha: 0, ease: "none",
    scrollTrigger: { trigger: ".hero", start: "top top", end: "78% top", scrub: true },
  });
  gsap.to(".hero-foot > *", {
    autoAlpha: 0, ease: "none",
    scrollTrigger: { trigger: ".hero", start: "top top", end: "40% top", scrub: true },
  });
  ScrollTrigger.create({
    trigger: ".hero", start: "top top", end: "bottom top", scrub: true,
    onUpdate(self) { sceneApi?.setScroll(self.progress); },
    onToggle(self) { sceneApi?.setRunning(self.isActive); },
  });
}

/* ── altitude systems ────────────────────── */
function initAltitude() {
  if (!hasGSAP) return;
  const thumb = $("#alt-thumb"), readout = $("#alt-readout"), heroAlt = $("#hero-alt");
  ScrollTrigger.create({
    trigger: document.body, start: "top top", end: "bottom bottom", scrub: true,
    onUpdate(self) {
      const alt = Math.round(self.progress * SUMMIT_ALT);
      /* ISA lapse rate: +15°C at sea level, −6.5°C per 1,000 m */
      const temp = Math.round(15 - (6.5 * alt) / 1000);
      const tempTxt = (temp > 0 ? "+" : "−") + Math.abs(temp) + "°C";
      if (readout) readout.textContent = "ALT " + alt.toLocaleString("en-US") + " M · " + tempTxt;
      if (thumb) thumb.style.top = (100 - self.progress * 100) + "%";
      if (heroAlt) heroAlt.textContent = alt.toLocaleString("en-US") + " M";
      /* weather rolls in past 4,200 m, builds toward the summit */
      const t = Math.max(0, Math.min(1, (alt - 4200) / 4300));
      window.__pvSetSnow?.(Math.pow(t, 1.35));
    },
  });
}

function initCoordsCycler() {
  const nameEl = $("#coords-name"), valEl = $("#coords-val");
  if (!nameEl || reduced) return;
  let idx = 3;
  setInterval(() => {
    idx = (idx + 1) % SUMMITS.length;
    scramble(nameEl, SUMMITS[idx].name, 0.8);
    scramble(valEl, SUMMITS[idx].coords, 1);
  }, 5000);
}

/* ── blueprint ───────────────────────────── */
function initBlueprint() {
  if (!hasGSAP || reduced) return;
  const bp = $(".blueprint");
  if (!bp) return;
  const draws = $$(".bp-draw", bp);
  const lens = draws.map(prepStroke);
  gsap.set([".bp-anno", ".bp-titleblock", ".bp-cross"], { autoAlpha: 0 });

  const tl = gsap.timeline({
    scrollTrigger: { trigger: bp, start: "top 72%" },
    onComplete: () => gsap.delayedCall(0.8, () => window.__pvProductReveal?.()),
  });
  tl.to(draws, {
    strokeDashoffset: 0,
    duration: 1.9, stagger: 0.08, ease: "power2.inOut",
  })
    .to(".bp-cross", { autoAlpha: 1, duration: 0.5 }, "-=0.9")
    .to(".bp-anno", { autoAlpha: 1, duration: 0.7, stagger: 0.14 }, "-=0.5")
    .to(".bp-titleblock", { autoAlpha: 1, duration: 0.8 }, "-=0.3");
}

/* ── traverse (Summit Series — seven dedications) ─── */
function initTraverse(lenis) {
  if (!hasGSAP || reduced || !$("#traverse-track")) return;
  const PEAKS = [
    { name: "EVEREST", elev: 8849 }, { name: "K2", elev: 8611 },
    { name: "KILIMANJARO", elev: 5895 }, { name: "MONT BLANC", elev: 4808 },
    { name: "FUJI", elev: 3776 }, { name: "ACONCAGUA", elev: 6961 },
    { name: "DENALI", elev: 6190 }, { name: "THE SERIES", elev: 8849 },
  ];
  const mm = gsap.matchMedia();
  mm.add("(min-width: 1025px) and (prefers-reduced-motion: no-preference)", () => {
    const track = $("#traverse-track");
    const cards = $$(".peak-card", track);
    const elevEl = $("#traverse-elev"), fillEl = $("#traverse-fill");
    const idxEl = $("#traverse-idx"), nameEl = $("#traverse-name");
    const amt = () => track.scrollWidth - innerWidth;
    let lastIdx = -1;
    const elevObj = { v: 8849 };

    const tween = gsap.to(track, {
      x: () => -amt(),
      ease: "none",
      scrollTrigger: {
        trigger: "#traverse",
        start: "top 10%",
        end: () => "+=" + amt() * 1.15,
        pin: true,
        scrub: 1.1,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onUpdate(self) {
          const p = self.progress;
          if (fillEl) fillEl.style.transform = `scaleX(${p})`;
          /* which card owns the viewport centre */
          const i = Math.min(PEAKS.length - 1, Math.round(p * (PEAKS.length - 1)));
          if (i !== lastIdx) {
            lastIdx = i;
            cards.forEach((c, ci) => c.classList.toggle("is-centred", ci === i));
            const peak = PEAKS[i];
            if (idxEl) idxEl.textContent = i < 7 ? String(i + 1).padStart(2, "0") : "07";
            if (nameEl) scramble(nameEl, peak.name, 0.45);
            if (elevEl) {
              gsap.to(elevObj, {
                v: peak.elev, duration: 0.9, ease: "power2.out",
                onUpdate: () => (elevEl.textContent = Math.round(elevObj.v).toLocaleString("en-US")),
              });
            }
          }
        },
      },
    });

    /* depth: cards lift + brighten as they pass the centre of the viewport */
    const centreTweens = cards.map((card) =>
      gsap.fromTo(card,
        { scale: 0.94, filter: "brightness(0.72)" },
        {
          scale: 1, filter: "brightness(1)",
          ease: "none",
          scrollTrigger: {
            trigger: card,
            containerAnimation: tween,
            start: "left right",
            end: "center center",
            scrub: true,
          },
        })
    );
    /* ...and settle back as they exit left */
    const exitTweens = cards.map((card) =>
      gsap.fromTo(card,
        { scale: 1, filter: "brightness(1)" },
        {
          scale: 0.94, filter: "brightness(0.72)",
          ease: "none",
          scrollTrigger: {
            trigger: card,
            containerAnimation: tween,
            start: "center center",
            end: "right left",
            scrub: true,
          },
        })
    );

    /* keyboard safety: the pinned track is translated inside an overflow:hidden
       viewport, so a Tab-focused card can sit off-screen with no visible focus.
       On focus, drive the pinned scroll so the focused card is brought into view
       (pointer/scroll behaviour is untouched — this only reacts to focus). */
    const onFocusIn = (e) => {
      const card = e.target.closest(".peak-card");
      if (!card || !track.contains(card)) return;
      const st = tween.scrollTrigger;
      const total = amt();
      if (!st || total <= 0) return;
      const px = Math.max(0, Math.min(total, card.offsetLeft + card.offsetWidth / 2 - innerWidth / 2));
      const y = st.start + (px / total) * (st.end - st.start);
      if (lenis) lenis.scrollTo(y, { duration: 0.5 });
      else st.scroll(y);
    };
    track.addEventListener("focusin", onFocusIn);

    return () => {
      track.removeEventListener("focusin", onFocusIn);
      tween.scrollTrigger?.kill();
      centreTweens.forEach((t) => t.scrollTrigger?.kill());
      exitTweens.forEach((t) => t.scrollTrigger?.kill());
      gsap.set(cards, { clearProps: "all" });
    };
  });

  /* mobile / tablet: native horizontal swipe. The card in the centre band
     gets the stronger image state — event-driven, no continuous scroll work. */
  mm.add("(max-width: 1024px)", () => {
    const vp = $("#traverse-viewport");
    if (!vp || typeof IntersectionObserver === "undefined") return;
    const cards = $$(".peak-card", vp);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) cards.forEach((c) => c.classList.toggle("is-centred", c === e.target));
      });
    }, { root: vp, rootMargin: "0px -49% 0px -49%", threshold: 0 });
    cards.forEach((c) => io.observe(c));
    return () => { io.disconnect(); cards.forEach((c) => c.classList.remove("is-centred")); };
  });

  /* ridge draw per card */
  if (!reduced) {
    $$(".peak-ridge path").forEach((p) => {
      prepStroke(p);
      gsap.to(p, {
        strokeDashoffset: 0, duration: 1.6, ease: "power2.out",
        scrollTrigger: { trigger: "#traverse", start: "top 70%" },
        delay: Math.random() * 0.5,
      });
    });
  }
}

/* ── camp 1 — the digital ascent ─────────── */
function initBasecamp() {
  if (!hasGSAP || reduced) return;
  const stage = $(".camp-stage");
  if (!stage) return;
  const route = $("#camp-route-path");
  if (route) prepStroke(route);
  gsap.set(".camp-wp", { autoAlpha: 0 });

  const tl = gsap.timeline({
    scrollTrigger: { trigger: ".basecamp", start: "top 60%" },
  });
  if (route) tl.to(route, { strokeDashoffset: 0, duration: 2.4, ease: "power2.inOut" }, 0);
  tl.to(".camp-wp", { autoAlpha: 1, duration: 0.55, stagger: 0.18 }, 0.8);
}

/* ── product stage (01) ──────────────────── */
function initProduct() {
  const stage = $("#product-stage");
  if (!stage) return;
  const btns = $$(".product-toggle-btn", stage);
  const tablist = stage.querySelector(".product-toggle");
  let userChose = false;
  const setView = (v) => {
    stage.dataset.view = v;
    btns.forEach((b) => {
      const on = b.dataset.view === v;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-selected", String(on));
      b.tabIndex = on ? 0 : -1; /* roving tabindex — WAI-ARIA tabs pattern */
    });
  };
  btns.forEach((b) => b.addEventListener("click", () => { userChose = true; setView(b.dataset.view); }));
  /* keyboard: arrow / Home / End move focus across the tabs and activate */
  if (tablist) tablist.addEventListener("keydown", (e) => {
    const i = btns.indexOf(document.activeElement);
    if (i < 0) return;
    let j = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") j = (i + 1) % btns.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") j = (i - 1 + btns.length) % btns.length;
    else if (e.key === "Home") j = 0;
    else if (e.key === "End") j = btns.length - 1;
    if (j < 0) return;
    e.preventDefault();
    userChose = true;
    setView(btns[j].dataset.view);
    btns[j].focus();
  });
  /* blueprint draws itself on scroll, then materialises into the product */
  window.__pvProductReveal = () => { if (!userChose) setView("product"); };
  setView(reduced ? "product" : "spec");
}

/* ── through the lens ────────────────────── */
function initLens() {
  const stage = $("#lens-stage");
  if (!stage) return;
  let pct = 55, targetPct = 55;
  let interacted = false, raf = 0, rect = null;

  /* one shared --cut var on the stage drives BOTH the clip-path and the divider
     position (CSS), so nothing re-rasterises while scrubbing — and a pre-JS
     render stays aligned. */
  const paint = () => {
    raf = 0;
    pct += (targetPct - pct) * 0.22;
    if (Math.abs(targetPct - pct) < 0.05) pct = targetPct;
    const r = Math.round(pct);
    stage.style.setProperty("--cut", pct + "%");
    stage.setAttribute("aria-valuenow", String(r));
    stage.setAttribute("aria-valuetext", r + "% filtered view");
    if (pct !== targetPct) raf = requestAnimationFrame(paint);
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(paint); };
  const clampPct = (v) => Math.max(6, Math.min(94, v));
  const setFromX = (clientX) => {
    if (!rect) rect = stage.getBoundingClientRect();
    targetPct = clampPct(((clientX - rect.left) / rect.width) * 100);
    schedule();
  };

  let dragging = false;
  stage.addEventListener("pointerdown", (e) => {
    dragging = true; interacted = true;
    rect = stage.getBoundingClientRect();
    try { stage.setPointerCapture(e.pointerId); } catch {}
    stage.classList.add("is-dragging");
    setFromX(e.clientX);
  }, { passive: true });
  /* drag anywhere; on desktop it also scrubs on hover so it feels alive */
  stage.addEventListener("pointermove", (e) => {
    if (!dragging && !finePointer) return;
    interacted = true;
    setFromX(e.clientX);
  }, { passive: true });
  const end = () => { dragging = false; rect = null; stage.classList.remove("is-dragging"); };
  addEventListener("pointerup", end, { passive: true });
  addEventListener("pointercancel", end, { passive: true });
  stage.addEventListener("pointerleave", () => { if (!dragging) rect = null; }, { passive: true });
  addEventListener("resize", () => { rect = null; schedule(); });

  stage.addEventListener("keydown", (e) => {
    const step = e.shiftKey ? 10 : 4;
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") { interacted = true; targetPct = clampPct(targetPct - step); schedule(); e.preventDefault(); }
    else if (e.key === "ArrowRight" || e.key === "ArrowUp") { interacted = true; targetPct = clampPct(targetPct + step); schedule(); e.preventDefault(); }
    else if (e.key === "Home") { interacted = true; targetPct = clampPct(6); schedule(); e.preventDefault(); }
    else if (e.key === "End") { interacted = true; targetPct = clampPct(94); schedule(); e.preventDefault(); }
  });

  requestAnimationFrame(() => { rect = stage.getBoundingClientRect(); paint(); rect = null; });

  if (hasGSAP && !reduced) {
    const o = { v: 12 };
    gsap.to(o, {
      v: 55, duration: 1.6, ease: "power3.inOut",
      onUpdate: () => { if (!interacted) { targetPct = o.v; pct = o.v; schedule(); } },
      scrollTrigger: { trigger: stage, start: "top 75%" },
    });
  }
}

/* ── page nav state / toast / product page ── */
function initPageChrome() {
  const path = location.pathname.split("/").pop() || "index.html";
  $$(".nav-link, .menu-link").forEach((a) => {
    const href = (a.getAttribute("href") || "").split("#")[0];
    if (href && href === path) a.setAttribute("aria-current", "page");
  });
}

let toastEl = null, toastTimer = null;
function showToast(msg) {
  if (!toastEl) {
    toastEl = document.createElement("div");
    toastEl.className = "toast mono";
    toastEl.setAttribute("role", "status");
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = msg;
  requestAnimationFrame(() => toastEl.classList.add("show"));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove("show"), 3200);
}
function initSoon() {
  document.addEventListener("click", (e) => {
    const t = e.target.closest("[data-soon]");
    if (!t) return;
    e.preventDefault();
    showToast(t.dataset.soon || "OPENS WITH FIRST ALLOCATION");
  });
}

function initFramePage() {
  if (document.body.dataset.page !== "product") return;
  const key = (new URLSearchParams(location.search).get("frame") || "evr").toLowerCase();
  const f = FRAMES[key] || FRAMES.evr;
  const k = FRAMES[key] ? key : "evr";
  document.title = `SUMMIT SERIES ${f.name} — PEAKVIEW ALPINE`;
  $$("[data-f]").forEach((el) => {
    const field = el.dataset.f;
    if (field === "sku") el.textContent = `PV-${f.code}-0${f.n}-CREVASSE`;
    else if (field === "num") el.textContent = `N° 0${f.n} / VII`;
    else if (field === "coords") el.textContent = `${f.lat}  ${f.lng}`;
    else if (f[field] !== undefined) el.textContent = f[field];
  });
  /* prev / next pager */
  const i = FRAME_ORDER.indexOf(k);
  const prev = FRAMES[FRAME_ORDER[(i + 6) % 7]];
  const next = FRAMES[FRAME_ORDER[(i + 1) % 7]];
  const prevA = $("#pd-prev"), nextA = $("#pd-next");
  if (prevA) {
    prevA.href = `product.html?frame=${FRAME_ORDER[(i + 6) % 7]}`;
    $("b", prevA).textContent = prev.name;
    $(".mono", prevA).textContent = `← N° 0${prev.n} — ${prev.ded.toUpperCase()}`;
  }
  if (nextA) {
    nextA.href = `product.html?frame=${FRAME_ORDER[(i + 1) % 7]}`;
    $("b", nextA).textContent = next.name;
    $(".mono", nextA).textContent = `N° 0${next.n} — ${next.ded.toUpperCase()} →`;
  }
  /* wishlist */
  const wl = $("#wishlist-btn");
  if (wl) {
    const store = "pv-wishlist";
    const getList = () => { try { return JSON.parse(localStorage.getItem(store)) || []; } catch { return []; } };
    const renderWl = () => {
      const saved = getList().includes(k);
      wl.querySelector(".btn-txt").textContent = saved ? "SAVED TO WISHLIST ✓" : "ADD TO WISHLIST";
    };
    wl.addEventListener("click", () => {
      const list = getList();
      const idx = list.indexOf(k);
      if (idx >= 0) list.splice(idx, 1); else list.push(k);
      try { localStorage.setItem(store, JSON.stringify(list)); } catch {}
      renderWl();
      showToast(idx >= 0 ? "REMOVED FROM WISHLIST" : `${f.name} — SAVED TO WISHLIST`);
    });
    renderWl();
  }
}

/* ── request access (Summit Series interest) ── */
function initAccess() {
  /* Homepage form: no email provider is approved yet — one is selected in
     the Shopify migration phase. Until that endpoint exists the form only
     validates locally, then says so plainly: nothing is sent, stored, or
     presented as a successful submission.
     INTEGRATION POINT: replace the preview branch below with the real
     provider call once approved. */
  const homeForm = $("#access-form");
  if (homeForm) {
    const input = $("#email", homeForm);
    const status = $("#access-status");
    const setStatus = (msg, isError) => {
      if (!status) return;
      status.hidden = false;
      status.textContent = msg;
      status.classList.toggle("is-error", !!isError);
    };
    homeForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const val = (input?.value || "").trim();
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      if (input) {
        input.classList.toggle("is-error", !ok);
        input.setAttribute("aria-invalid", String(!ok));
      }
      if (!ok) {
        setStatus("ENTER A VALID EMAIL ADDRESS.", true);
        if (hasGSAP && !reduced) gsap.fromTo(homeForm, { x: 0 }, { x: 8, duration: 0.07, repeat: 5, yoyo: true, clearProps: "x" });
        input?.focus();
        return;
      }
      /* honest preview — provider not connected, address not kept */
      setStatus("PREVIEW — EMAIL SIGN-UP ISN’T CONNECTED YET. NOTHING WAS SENT OR STORED.", false);
    });
    input?.addEventListener("input", () => {
      if (!input.classList.contains("is-error")) return;
      input.classList.remove("is-error");
      input.setAttribute("aria-invalid", "false");
      if (status && status.classList.contains("is-error")) status.hidden = true;
    });
  }

  /* Inner-page waitlist forms (apparel / collection) keep their previous
     behavior untouched until those pages reach this milestone. */
  $$("form.access-form").forEach((form) => {
    if (form === homeForm) return;
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const val = (input?.value || "").trim();
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      if (!ok) {
        input.classList.add("is-error");
        input.setAttribute("aria-invalid", "true");
        input.placeholder = "ENTER A VALID EMAIL";
        if (hasGSAP && !reduced) gsap.fromTo(form, { x: 0 }, { x: 8, duration: 0.07, repeat: 5, yoyo: true, clearProps: "x" });
        return;
      }
      const done = document.createElement("div");
      done.className = "access-done";
      done.setAttribute("role", "status");
      done.innerHTML =
        `<p class="access-done-title">REQUEST RECEIVED</p>` +
        `<p class="access-done-sub">You're on the list for the first ascent. Details follow by email.</p>`;
      form.replaceWith(done);
      if (hasGSAP && !reduced) gsap.from(done, { y: 18, autoAlpha: 0, duration: 0.8, ease: "power3.out" });
    });
  });
}

/* ── footer giant drift ──────────────────── */
function initFooter() {
  if (!hasGSAP || reduced) return;
  /* v2 footer (homepage) closes quietly — shallower drift; inner pages
     keep their previous depth until their milestone */
  gsap.from("#footer-giant", {
    yPercent: $(".footer--v2") ? 16 : 42, ease: "none",
    scrollTrigger: { trigger: ".footer", start: "top 96%", end: "top 40%", scrub: true },
  });
}

/* ── preloader ───────────────────────────── */
function runPreloader(onDone) {
  const pre = $("#preloader");
  if (!pre || reduced || !hasGSAP) { window.__pvPreDone = true; pre && pre.remove(); onDone(); return; }
  const num = $("#pre-alt-num");
  const bar = $("#pre-bar-fill");
  const coords = $("#pre-coords");
  const alt = { v: 0 };

  scramble(coords, SUMMITS[0].coords, 1.4);
  setTimeout(() => scramble(coords, SUMMITS[3].coords, 1), 1500);

  const tl = gsap.timeline({
    onComplete: () => { window.__pvPreDone = true; pre.remove(); onDone(); },
  });
  tl.to(".pre-mark", { autoAlpha: 1, scale: 1, duration: 1.1, ease: "power3.out" }, 0)
    .to(alt, {
      v: SUMMIT_ALT, duration: 2.2, ease: "power3.inOut",
      onUpdate: () => (num.textContent = Math.round(alt.v).toLocaleString("en-US")),
    }, 0.15)
    .to(bar, { width: "100%", duration: 2.35, ease: "power2.inOut" }, 0.15)
    .to(".pre-inner, .pre-corner", { autoAlpha: 0, y: -14, duration: 0.55, ease: "power2.in", stagger: 0.02 }, "+=0.25")
    .to(pre, { clipPath: "inset(0% 0% 100% 0%)", duration: 0.95, ease: "power4.inOut" }, "-=0.15");
}

/* ── deferred section imagery ───────────── */
/* SVG <image> ignores loading="lazy", so the two heavy full-bleed concept
   photos (camp-ibiri, capsule-flat) ship as data-src and get their href set
   only as their section nears the viewport — no eager first-paint fetch, exact
   same composition/crop, and no layout shift (geometry is fixed by w/h). */
function initDeferredMedia() {
  const imgs = $$("image[data-src]");
  if (!imgs.length) return;
  const load = (im) => {
    const src = im.getAttribute("data-src");
    if (!src) return;
    im.setAttribute("href", src);
    im.removeAttribute("data-src");
  };
  if (typeof IntersectionObserver === "undefined") { imgs.forEach(load); return; }
  const hosts = new Map();
  imgs.forEach((im) => {
    const host = im.closest("svg") || im;
    if (!hosts.has(host)) hosts.set(host, []);
    hosts.get(host).push(im);
  });
  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      (hosts.get(e.target) || []).forEach(load);
      obs.unobserve(e.target);
    });
  }, { rootMargin: "700px 0px" });
  hosts.forEach((_, host) => io.observe(host));
}

/* ── boot ────────────────────────────────── */
async function boot() {
  if (window.__pvBooted) return;
  window.__pvBooted = true;
  window.__pvLoaded = true;
  document.body.classList.add("is-booting");

  /* brand mark — inject the ibex symbol, swap every placeholder */
  try {
    const { mountLogo } = await import("./logo.js");
    mountLogo();
  } catch (err) { console.warn("PEAKVIEW: logo unavailable —", err); }

  /* Lenis smooth scroll */
  let lenis = null;
  if (typeof Lenis !== "undefined" && !reduced && hasGSAP) {
    lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1.02 });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  /* three.js terrain (graceful) */
  let sceneApi = null;
  const terrainCanvas = $("#terrain");
  if (terrainCanvas && (!reduced || STATIC)) {
    try {
      const mod = await import("./scene.js");
      sceneApi = mod.initScene(terrainCanvas);
      window.__pvScene = sceneApi;
    } catch (err) {
      console.warn("PEAKVIEW: terrain unavailable —", err);
      terrainCanvas.remove();
    }
  } else if (terrainCanvas) {
    terrainCanvas.remove();
  }
  if (STATIC) {
    document.documentElement.classList.add("static", "no-anim");
    sceneApi?.setIntro(1);
  }
  if (sceneApi && finePointer) {
    addEventListener("pointermove", (e) => {
      sceneApi.setPointer((e.clientX / innerWidth - 0.5) * 2, (e.clientY / innerHeight - 0.5) * 2);
    });
  }

  /* hero title split (before preloader ends) — PEAKVIEW / ALPINE */
  const chars = hasGSAP && !reduced
    ? $$("[data-split-chars]").flatMap((line) => splitChars(line))
    : [];
  if (chars.length) gsap.set(chars, { yPercent: 118 });
  if (hasGSAP && !reduced) gsap.set("[data-hero-fade], .nav", { autoAlpha: 0 });
  if (hasGSAP && STATIC) ScrollTrigger.config({ ignoreMobileResize: true });

  initNav(lenis);
  initPageChrome();
  initSoon();
  initFramePage();
  initSnow();
  initDeferredMedia();
  if (STATIC) window.__pvSetSnow?.(0.35);
  initCursor();
  initMarquee();
  initAltitude();
  initCoordsCycler();

  /* dev flag: ?only=<element id> — isolate a block at top of page (design QA) */
  const ONLY = new URLSearchParams(location.search).get("only");
  if (ONLY) {
    const target = document.getElementById(ONLY);
    if (target) {
      const section = target.closest("main > *, body > *") || target;
      $$(".hero, .marquee, main > section, footer").forEach((el) => {
        if (el !== section) el.style.display = "none";
      });
      if (section !== target) {
        let n = target;
        while (n && n !== section) {
          let sib = n.previousElementSibling;
          while (sib) { sib.style.display = "none"; sib = sib.previousElementSibling; }
          n = n.parentElement;
        }
      }
      $("#nav").style.display = "none";
    }
  }

  /* dev flag: ?at=<element id> — jump before first paint (design QA) */
  const AT = new URLSearchParams(location.search).get("at");
  if (AT) {
    const jump = () => {
      const target = document.getElementById(AT);
      if (target) scrollTo({ top: target.getBoundingClientRect().top + scrollY - 90, behavior: "instant" });
    };
    jump();
    setTimeout(jump, 350);
  }

  let booted = false;
  const finishBoot = () => {
    if (booted) return;
    booted = true;
    try {
      if (hasGSAP && !reduced && !document.documentElement.classList.contains("no-anim")) {
        heroIntroTimeline(chars, sceneApi);
      } else if (sceneApi) sceneApi.setIntro(1);
      initHeroScroll(sceneApi);
      initReveals();
      initProduct();
      initLens();
      initBlueprint();
      initTraverse(lenis);
      initBasecamp();
      initAccess();
      initFooter();
      if (hasGSAP && !reduced) {
        /* summit weather: gust while the access section is in view */
        if ($(".access")) {
          const gust = { v: 1 };
          ScrollTrigger.create({
            trigger: ".access", start: "top 60%", end: "bottom 20%",
            onToggle(self) {
              gsap.to(gust, {
                v: self.isActive ? 1.7 : 1, duration: 2.4, ease: "sine.inOut",
                onUpdate: () => window.__pvGust?.(gust.v),
              });
            },
          });
        }
        /* camp 1 device + card drift slightly as you pass — restrained depth */
        if ($(".bc-app")) {
          gsap.to(".bc-app", {
            y: -24, ease: "none",
            scrollTrigger: { trigger: ".basecamp", start: "top 80%", end: "bottom 20%", scrub: 1 },
          });
        }
        if ($(".camp-card")) {
          gsap.to(".camp-card", {
            y: -12, ease: "none",
            scrollTrigger: { trigger: ".basecamp", start: "top 80%", end: "bottom 20%", scrub: 1 },
          });
        }
      }
      if (hasGSAP) requestAnimationFrame(() => ScrollTrigger.refresh());
    } catch (err) {
      console.warn("PEAKVIEW: animation init failed —", err);
      document.documentElement.classList.add("no-anim");
      if (hasGSAP) gsap.set("[data-hero-fade], .nav, .hero-title .char", { clearProps: "all" });
      if (sceneApi) sceneApi.setIntro(1);
    }
  };
  window.__pvFinishBoot = finishBoot;

  /* ── Shopify Theme Editor lifecycle (design mode only) ──
     Narrow, non-duplicating: on section reload, re-reveal that section's
     content and refresh ScrollTrigger; on unload, kill only the triggers
     scoped to the removed section. Does NOT re-run global systems (snow,
     marquee, cursor, intervals, observers), so nothing is duplicated. */
  if (window.Shopify && window.Shopify.designMode) {
    const revealIn = (root) => {
      if (!root) return;
      root.querySelectorAll("[data-reveal], [data-split], [data-split-chars], [data-hero-fade]").forEach((el) => {
        if (hasGSAP) gsap.set(el, { clearProps: "opacity,visibility,transform,y,yPercent", autoAlpha: 1 });
        else { el.style.opacity = ""; el.style.visibility = ""; }
      });
    };
    document.addEventListener("shopify:section:load", (e) => {
      revealIn(e.target);
      if (hasGSAP) ScrollTrigger.refresh();
    });
    document.addEventListener("shopify:section:unload", (e) => {
      if (hasGSAP) {
        ScrollTrigger.getAll().forEach((st) => { if (st.trigger && e.target.contains(st.trigger)) st.kill(); });
        ScrollTrigger.refresh();
      }
    });
    document.addEventListener("shopify:section:select", (e) => {
      if (e.target && e.target.scrollIntoView) e.target.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  runPreloader(finishBoot);
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
